'use client';

import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { albumApi, type Album, type ProjectAlbumsEntry } from '@/features/design/album';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { AlbumCoverView } from '@/features/design/history/ui/AlbumCoverView';
import {
  SectionHeader,
  SectionPanel,
  ScrollableCarousel,
  PANEL_HEIGHT,
  CARD_ASPECT_RATIO,
  type SectionAction,
} from './section-components';
import { OnboardingEmptyState } from './OnboardingEmptyState';

// Single album card using AlbumCoverView (matching Apple/Android)
interface AlbumCardProps {
  album: Album;
  concept: ProjectAlbumsEntry['concept'];
  projectId: string;
}

function AlbumCard({ album, concept, projectId }: AlbumCardProps) {
  // Always navigate to the concept's Album tab (same as the dashboard
  // carousel) — opening the PDF itself happens from the concept page.
  const href = `/project/${projectId}/concept/${concept.id}?tab=album`;

  // Two changes vs. the original `<Link rounded-lg overflow-hidden>` wrapper:
  //   1) No `overflow-hidden` — AlbumCoverView paints its own rounded paper
  //      and drop-shadow; clipping at the outer Link would chop the shadow
  //      at the rounded edge (same trap as Apple's `clipShape` and Android's
  //      `Card` wrapper).
  //   2) `pb-7` under the cover gives the soft drop-shadow room to render
  //      INSIDE the Link before `ScrollableCarousel`'s `overflow-x-auto`
  //      (which forces `overflow-y` to compute as `auto`) chops it. Dashboard
  //      gets the same effect implicitly from its info-text block under
  //      `AlbumCoverView`; this section has no info block, so we add a
  //      bottom cushion. AlbumCoverView's bottom-most shadow layer is
  //      `0 8px 16px`, so ~28px of cushion covers the soft tail.
  return (
    <Link href={href} className="flex-shrink-0 pb-7">
      <div
        style={{
          height: PANEL_HEIGHT - 24,
          width: (PANEL_HEIGHT - 24) * CARD_ASPECT_RATIO,
        }}
      >
        <AlbumCoverView
          coverImageUrl={concept.main_render_url}
          conceptTitle={concept.title}
          conceptNumber={concept.project_sequence_number}
          format={album.format}
          createdAt={album.created_at}
          status={album.status}
          albumId={album.id}
          albumTitle={album.album_title}
        />
      </div>
    </Link>
  );
}

// Project Albums Section
interface ProjectAlbumsSectionProps {
  projectId: string;
}

export function ProjectAlbumsSection({ projectId }: ProjectAlbumsSectionProps) {
  const t = useTranslations('album');
  const tOnboarding = useTranslations('onboarding');

  const { data: projectAlbums = [], isLoading } = useQuery({
    queryKey: cacheKeys.projectAlbums.list(projectId),
    queryFn: () => albumApi.listByProject(projectId),
    enabled: !!projectId,
  });

  // Flatten: each album as a separate card (matching Apple/Android)
  const flatAlbums = projectAlbums.flatMap((entry) =>
    entry.albums.map((album) => ({ album, concept: entry.concept }))
  );

  const actions: SectionAction[] = [
    {
      label: t('albums'),
      href: `/project/${projectId}/albums`,
    },
  ];

  return (
    <div className="space-y-4">
      <SectionHeader
        title={t('albums')}
        actions={actions}
        count={flatAlbums.length}
      />
      {isLoading ? (
        <SectionPanel>
          <div className="h-full flex items-center gap-2 p-3 overflow-hidden">
            <Skeleton className="h-full aspect-[4/3] rounded-lg flex-shrink-0" />
            <Skeleton className="h-full aspect-[4/3] rounded-lg flex-shrink-0" />
          </div>
        </SectionPanel>
      ) : flatAlbums.length > 0 ? (
        <ScrollableCarousel>
          {flatAlbums.map(({ album, concept }) => (
            <AlbumCard
              key={album.id}
              album={album}
              concept={concept}
              projectId={projectId}
            />
          ))}
        </ScrollableCarousel>
      ) : (
        <SectionPanel>
          <OnboardingEmptyState
            icon="albums"
            step={5}
            description={tOnboarding('albums')}
          />
        </SectionPanel>
      )}
    </div>
  );
}
